import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import ConfirmationModal from "../../Components/ConfirmationModal";
import AdvancePagination from "../../Components/Pagination";
import SearchBar from "../../Components/SearchBar";
import SelectItemsPerPage from "../../Components/SelectItemsPerPage";
import SpinnerBorder from "../../Components/SpinnerBorder";
import SpinnerGrow from "../../Components/SpinnerGrow";
import ListHistory from "./ListHistory";
import SearchHistoryService from "../../Services/SearchHistoryService";

const pageTitle = "Search History";
export default function MainSearchHistory() {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [itemsLength, setItemsLength] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const [searchHistoryData, setSearchHistoryData] = useState([]);

  const getSearchHistoryData = async () => {
    setUpdating(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        query: searchQuery,
      };
      const response = await SearchHistoryService.getSearchHistory(params);

      if (response.data.success) {
        setSearchHistoryData(response.data.data || []);
        setItemsLength(response.data.pagination?.total || 0);
        setLoading(false);
      } else {
        toast.error(response.data.message || "Failed to fetch search history");
        setSearchHistoryData([]);
        setItemsLength(0);
      }
    } catch (error) {
      console.error("Error fetching search history:", error);
      toast.error("An error occurred while fetching search history");
      setSearchHistoryData([]);
      setItemsLength(0);
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    getSearchHistoryData();
  }, [searchQuery, currentPage, pageSize]);

  const handleSearchInput = (e) => {
    e.preventDefault();
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const clearSearchQuery = () => {
    setSearchQuery("");
  };

  const handlePageSizeChange = (event) => {
    event.preventDefault();
    setPageSize(parseInt(event.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [itemToConfirm, setItemToConfirm] = useState(null);

  const handleDelete = (e, data) => {
    e.preventDefault();
    setItemToConfirm(data);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmation(false);
    if (itemToConfirm) {
      try {
        await toast.promise(
          SearchHistoryService.deleteSearchHistory(itemToConfirm.id),
          {
            loading: "Deleting...",
            success: <b>Search history deleted successfully!</b>,
            error: <b>Could not delete search history</b>,
          }
        );
        setItemToConfirm(null);
        getSearchHistoryData(); // updates the data after deletion
      } catch (error) {
        toast.error("An error occurred while deleting search history");
      }
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setItemToConfirm(null);
  };

  if (loading) return <SpinnerGrow />;

  return (
    <>
      <div className="row px-5">
        <div className="text-center fs-1 text-uppercase col-12">
          {pageTitle}
        </div>
        <div className="col-6"> </div>
        <div className="col-6 d-flex justify-content-end">
          <SearchBar
            searchQuery={searchQuery}
            handleSearchInput={handleSearchInput}
            clearSearchQuery={clearSearchQuery}
            searchTitle={`Search ${pageTitle}`}
          />
          <SelectItemsPerPage handlePageSizeChange={handlePageSizeChange} />
        </div>
        {updating ? (
          <SpinnerBorder />
        ) : searchHistoryData.length === 0 ? (
          <div className="mt-5 text-center fs-1">No Data Found</div>
        ) : (
          <div className="col-12 p-4">
            <ListHistory
              dataList={searchHistoryData}
              handleDelete={handleDelete}
              getData={getSearchHistoryData}
              dataService={SearchHistoryService}
            />
            <AdvancePagination
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              totalItems={itemsLength}
              pageSize={pageSize}
            />
            <ConfirmationModal
              show={showConfirmation}
              onHide={handleCloseConfirmation}
              onConfirm={handleConfirmDelete}
              message={
                itemToConfirm
                  ? `Are you sure you want to delete the search history for "${itemToConfirm.query}"?`
                  : ""
              }
              title={itemToConfirm?.query}
            />
          </div>
        )}
      </div>
    </>
  );
}
